/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import { useIntl } from 'react-intl';
import { Form, Input, Checkbox, Button, Divider, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery, useMutation } from 'react-query';
import {
  getCurrentUserContext,
  submitProjectApplication,
} from '~src/services/api/user';
import { ProjectApplicationPayload } from '~src/types/db';
import { useStyles } from '~src/hooks/useStyles';

interface CreateProjectFormProps {
  onClose: () => void;
  onSuccess: () => void;
}

const { TextArea } = Input;

export const CreateProjectForm: React.FC<CreateProjectFormProps> = ({
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const styles = useStyles(styleFn);

  // Fetch user context to prefill form
  const { data: userContext } = useQuery(
    ['user-context-project-app'],
    getCurrentUserContext,
    {
      onSuccess: (data) => {
        form.setFieldsValue({
          cvr: data?.cvr || data?.customer?.customer_cvr,
          email: data?.mail || data?.current_user?.user_email,
          phone:
            data?.phone ||
            data?.current_user?.user_mobile ||
            data?.current_user?.user_phone,
        });
      },
      retry: false, // Don't retry if 204/401
    }
  );

  // Submit mutation
  const [submitApplication, { isLoading }] = useMutation(
    submitProjectApplication,
    {
      onSuccess: () => {
        message.success(
          intl.formatMessage({ id: 'project-application-success-message' })
        );
        form.resetFields();
        onSuccess(); // Fully close drawer
      },
      onError: () => {
        message.error(
          intl.formatMessage({ id: 'project-application-error-message' })
        );
      },
    }
  );

  // Transform form values to API payload
  const transformToPayload = (values: any): ProjectApplicationPayload => {
    return {
      project: {
        name: values.projectName,
        address:
          values.address?.road || values.address?.number
            ? {
                road: values.address?.road,
                number: values.address?.number,
              }
            : undefined,
        postal_code: values.postalCode,
      },
      owner: values.cvr ? { cvr: values.cvr } : undefined,
      contact_information: {
        email: values.email,
        phone: values.phone,
        contract_number: values.contractNumber,
      },
      modules: {
        project: true,
        tender: {
          checked: values.moduleTender ?? true,
        },
        modeling_3d: {
          checked: values.module3DModeling ?? false,
        },
      },
      remark: values.remark,
    };
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = transformToPayload(values);
      submitApplication(payload);
    } catch (error) {
      // Validation failed
      console.error('Form validation failed:', error);
    }
  };

  return (
    <div css={styles.formContainer}>
      {/* Header */}
      <div css={styles.header}>
        <button onClick={onClose} css={styles.backButton}>
          <FontAwesomeIcon icon={['far', 'arrow-left']} />
        </button>
        <h3 css={styles.title}>
          {intl.formatMessage({ id: 'create-new-project' })}
        </h3>
      </div>

      {/* Introduction */}
      <div
        style={{
          padding: '16px 20px',
          fontSize: '14px',
          color: styles.sectionHeader.color,
          lineHeight: '1.6',
        }}>
        {intl.formatMessage({ id: 'introduction-text' })}
      </div>

      {/* Form */}
      <Form
        form={form}
        layout="vertical"
        style={{
          padding: '20px',
          paddingBottom: '24px',
        }}
        initialValues={{
          moduleTender: true,
          module3DModeling: false,
        }}>
        {/* Project Section */}
        <Divider style={styles.sectionHeader}>
          {intl.formatMessage({ id: 'project-section-header' })}
        </Divider>

        <Form.Item
          name="projectName"
          label={intl.formatMessage({ id: 'project-name' })}
          rules={[
            {
              required: true,
              message: intl.formatMessage({ id: 'project-name-required' }),
            },
          ]}>
          <Input />
        </Form.Item>

        <Form.Item
          label={intl.formatMessage({ id: 'address' })}
          style={{ marginBottom: 0 }}>
          <Form.Item
            name={['address', 'road']}
            style={{ display: 'inline-block', width: 'calc(70% - 8px)' }}>
            <Input placeholder={intl.formatMessage({ id: 'address-road' })} />
          </Form.Item>
          <Form.Item
            name={['address', 'number']}
            style={{
              display: 'inline-block',
              width: '30%',
              margin: '0 0 0 8px',
            }}>
            <Input placeholder={intl.formatMessage({ id: 'address-number' })} />
          </Form.Item>
        </Form.Item>

        <Form.Item
          name="postalCode"
          label={intl.formatMessage({ id: 'postal-code' })}>
          <Input />
        </Form.Item>

        {/* Owner Section */}
        <Divider style={styles.sectionHeader}>
          {intl.formatMessage({ id: 'owner-section-header' })}
        </Divider>

        <Form.Item name="cvr" label={intl.formatMessage({ id: 'cvr-number' })}>
          <Input />
        </Form.Item>

        {/* Contact Information Section */}
        <Divider style={styles.sectionHeader}>
          {intl.formatMessage({ id: 'contact-information-section-header' })}
        </Divider>

        <Form.Item
          name="email"
          label={intl.formatMessage({ id: 'email' })}
          rules={[
            {
              required: true,
              type: 'email',
              message: intl.formatMessage({ id: 'email-required' }),
            },
          ]}>
          <Input />
        </Form.Item>

        <Form.Item
          name="phone"
          label={intl.formatMessage({ id: 'phone' })}
          rules={[
            {
              required: true,
              message: intl.formatMessage({ id: 'phone-required' }),
            },
          ]}>
          <Input />
        </Form.Item>

        <Form.Item
          name="contractNumber"
          label={intl.formatMessage({ id: 'contract-number' })}>
          <Input />
        </Form.Item>

        {/* Modules Section */}
        <Divider style={styles.sectionHeader}>
          {intl.formatMessage({ id: 'modules-section-header' })}
        </Divider>

        <Form.Item
          name="moduleProject"
          valuePropName="checked"
          initialValue={true}>
          <Checkbox disabled>
            {intl.formatMessage({ id: 'module-project' })}
          </Checkbox>
        </Form.Item>

        <Form.Item
          name="moduleTender"
          valuePropName="checked"
          initialValue={true}>
          <Checkbox>{intl.formatMessage({ id: 'module-tender' })}</Checkbox>
        </Form.Item>

        <Form.Item
          name="module3DModeling"
          valuePropName="checked"
          initialValue={false}>
          <Checkbox>
            {intl.formatMessage({ id: 'module-3d-modeling' })}
          </Checkbox>
        </Form.Item>

        <Form.Item name="remark" label={intl.formatMessage({ id: 'remark' })}>
          <TextArea
            rows={4}
            placeholder={intl.formatMessage({ id: 'remark-placeholder' })}
          />
        </Form.Item>

        {/* Disclaimer */}
        <div
          style={{
            padding: '0 0 16px 0',
            fontSize: '12px',
            color: styles.sectionHeader.color,
            lineHeight: '1.5',
            fontStyle: 'italic',
            opacity: 0.8,
          }}>
          {intl.formatMessage({ id: 'disclaimer-text' })}
        </div>

        {/* Submit Button */}
        <Form.Item>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={isLoading}
            style={styles.submitButton}
            block>
            {intl.formatMessage({ id: 'submit-project-application' })}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

const styleFn = (theme: any) => ({
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'inherit',
    overflowY: 'auto',
    padding: 0,
  },
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 20px',
    marginBottom: 0,
    backgroundColor:
      window.sessionStorage.getItem('appState') === 'superUser'
        ? theme.colors.primaryLight
        : window.sessionStorage.getItem('appState') === 'timeMachine'
        ? theme.colors.background.primaryLight
        : theme.colors.white,
    borderBottom: `1px solid ${theme.colors.darkGray}`,
  },
  backButton: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.colors.text.primary,
    transition: 'color 0.2s',
    '&:hover': {
      color: theme.colors.primary,
    },
    svg: {
      fontSize: '18px',
    },
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    margin: 0,
    color: theme.colors.text.primary,
  },
  sectionHeader: {
    fontSize: '14px',
    fontWeight: 600,
    margin: '20px 0 12px 0',
    color: theme.colors.text.primary,
  },
  submitButton: {
    marginTop: '16px',
    fontSize: '16px',
    fontWeight: 600,
    height: '40px',
  },
});
